import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex, Stack, Text, createStyles } from '@mantine/core';
import { IconList, IconListNumbers, IconSlash } from '@tabler/icons-react';
import { Editor, FloatingMenu } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { FLOATING_MENU_SELECTOR, applyEditorFloatingMenuCommand } from 'shared/constants/editor';
import { Z_INDEX } from 'shared/constants/z-index';
interface SmartEditorFloatingMenuProps {
  editor: Editor;
  isLinkInputActive: boolean;
}

type MenuCommand = {
  icon: React.ReactNode;
  label: string;
  description: string;
  shortcut?: string;
  command: () => void;
};

const useStyles = createStyles((theme) => ({
  notionMenuContainer: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[2]}`,
    boxShadow: theme.shadows.md,
    width: '280px',
    zIndex: Z_INDEX.FLOATING_ELEMENTS
  },
  menuHeader: {
    borderBottom: `1px solid ${theme.colors.gray[1]}`,
    padding: '6px 10px',
    marginBottom: '2px'
  },
  menuOption: {
    padding: '8px 10px',
    cursor: 'pointer',
    gap: '10px',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  menuOptionSelected: {
    padding: '8px 10px',
    cursor: 'pointer',
    gap: '10px',
    backgroundColor: theme.colors.gray[0]
  },
  menuItemIcon: {
    width: '22px',
    height: '22px',
    borderRadius: '4px',
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontWeight: 500,
    fontSize: '13px'
  },
  menuLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: theme.colors.dark[7]
  },
  menuDescription: {
    fontSize: '12px',
    color: theme.colors.gray[6]
  },
  commandShortcut: {
    position: 'absolute',
    right: '10px',
    color: theme.colors.gray[5],
    fontSize: '12px',
    fontWeight: 400
  }
}));

export function SmartEditorFloatingMenu({ editor, isLinkInputActive }: SmartEditorFloatingMenuProps) {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [searchText, setSearchText] = useState('');

  const [selectedItemIndex, setSelectedItemIndex] = useState(0);

  // Define menu items
  const menuItems: MenuCommand[] = [
    {
      icon: <Text size="xs">H1</Text>,
      label: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_1.LABEL'),
      description: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_1.DESCRIPTION'),
      shortcut: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_1.SHORTCUT'),
      command: () => {
        editor.chain().focus().setHeading({ level: 1 }).run();
      }
    },
    {
      icon: <Text size="xs">H2</Text>,
      label: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_2.LABEL'),
      description: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_2.DESCRIPTION'),
      shortcut: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_2.SHORTCUT'),
      command: () => {
        editor.chain().focus().setHeading({ level: 2 }).run();
      }
    },
    {
      icon: <Text size="xs">H3</Text>,
      label: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_3.LABEL'),
      description: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_3.DESCRIPTION'),
      shortcut: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.HEADING_3.SHORTCUT'),
      command: () => {
        editor.chain().focus().setHeading({ level: 3 }).run();
      }
    },
    {
      icon: <IconList size={14} />,
      label: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.BULLETED_LIST.LABEL'),
      description: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.BULLETED_LIST.DESCRIPTION'),
      shortcut: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.BULLETED_LIST.SHORTCUT'),
      command: () => {
        editor.chain().focus().toggleBulletList().run();
      }
    },
    {
      icon: <IconListNumbers size={14} />,
      label: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.NUMBERED_LIST.LABEL'),
      description: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.NUMBERED_LIST.DESCRIPTION'),
      shortcut: t('SMART_EDITOR.BUBBLE_MENU.ITEMS.NUMBERED_LIST.SHORTCUT'),
      command: () => {
        editor.chain().focus().toggleOrderedList().run();
      }
    }
  ];

  const filteredMenuItems = useMemo(() => {
    if (!searchText) return menuItems;
    const searchTextLower = searchText.toLowerCase();

    return menuItems.filter((menuItem) => {
      const labelLower = menuItem.label.toLowerCase();
      const descriptionLower = menuItem.description.toLowerCase();
      return labelLower.includes(searchTextLower) || descriptionLower.includes(searchTextLower);
    });
  }, [menuItems, searchText]);

  // Navigation helpers
  const setPreviousItemIndex = () =>
    setSelectedItemIndex((prevIndex) => (prevIndex - 1 + filteredMenuItems.length) % filteredMenuItems.length);
  const setNextItemIndex = () => setSelectedItemIndex((prevIndex) => (prevIndex + 1) % filteredMenuItems.length);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // If link input is active, don't process keyboard events
      if (isLinkInputActive) return;

      // If the floating menu is not visible, don't process keyboard events
      const floatingMenu = document.querySelector(FLOATING_MENU_SELECTOR);
      if (!floatingMenu) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setNextItemIndex();
          break;
        case 'ArrowUp':
          event.preventDefault();
          setPreviousItemIndex();
          break;
        case 'Enter': {
          // We need to completely take over the Enter key behavior
          event.preventDefault();
          event.stopPropagation();

          const selectedItem = filteredMenuItems[selectedItemIndex];
          if (selectedItem) applyEditorFloatingMenuCommand(editor, selectedItem.command);
          break;
        }
        default:
          break;
      }
    },
    [filteredMenuItems, selectedItemIndex, editor, isLinkInputActive]
  );

  // Add event listener for keyboard navigation
  useEffect(() => {
    // Use a more specific check to avoid duplicate handlers
    const handleKeyboardNavigation = (event: KeyboardEvent) => {
      // Only process if menu is visible
      const floatingMenu = document.querySelector(FLOATING_MENU_SELECTOR);
      if (!floatingMenu) return;
      handleKeyDown(event);
    };

    // Add with capture to intercept before TipTap
    document.addEventListener('keydown', handleKeyboardNavigation, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeyboardNavigation, { capture: true });
    };
  }, [handleKeyDown]);

  const resetMenu = () => {
    setSelectedItemIndex(0);
    setSearchText('');
  };

  return (
    <FloatingMenu
      editor={editor}
      tippyOptions={{
        placement: 'bottom',
        zIndex: Z_INDEX.FLOATING_ELEMENTS,
        onHide: () => resetMenu(),
        onShow: () => resetMenu()
      }}
      shouldShow={({ editor, state }) => {
        if (!editor.isEditable) return false;

        // Get text before cursor
        const $from = state.selection.$from;
        const currentLineStart = $from.start();
        const currentLineText = state.doc.textBetween(currentLineStart, $from.pos, '\n');

        // Check if line starts with a slash
        if (currentLineText.startsWith('/')) {
          // Extract the search text after the slash
          const newSearchText = currentLineText.substring(1);
          // Only update if changed to avoid re-renders
          if (newSearchText !== searchText) {
            setSearchText(newSearchText);
            // Reset selection when search text changes
            setSelectedItemIndex(0);
          }
          return true;
        }

        return false;
      }}>
      <Stack spacing={0} className={classes.notionMenuContainer}>
        <Box className={classes.menuHeader}>
          <Text size="xs" weight={500} color="dark.5">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconSlash size={12} style={{ marginRight: 8 }} />
              {searchText
                ? t('SMART_EDITOR.BUBBLE_MENU.RESULTS_FOR', { searchText })
                : t('SMART_EDITOR.BUBBLE_MENU.BASIC_BLOCKS')}
            </Box>
          </Text>
        </Box>

        {filteredMenuItems.length > 0 ? (
          filteredMenuItems.map((item, index) => (
            <Flex
              align="center"
              key={index}
              className={index === selectedItemIndex ? classes.menuOptionSelected : classes.menuOption}
              onMouseMove={() => setSelectedItemIndex(index)}
              onClick={() => applyEditorFloatingMenuCommand(editor, item.command)}>
              <Flex align="center" justify="center" className={classes.menuItemIcon}>
                {item.icon}
              </Flex>
              <Stack spacing={1}>
                <Text className={classes.menuLabel}>{item.label}</Text>
                <Text className={classes.menuDescription}>{item.description}</Text>
              </Stack>
              {item.shortcut && <Text className={classes.commandShortcut}>{item.shortcut}</Text>}
            </Flex>
          ))
        ) : (
          <Box p="xs">
            <Text size="sm" color="gray.6">
              {t('SMART_EDITOR.BUBBLE_MENU.NO_RESULTS')}
            </Text>
          </Box>
        )}
      </Stack>
    </FloatingMenu>
  );
}
